import type { TranscriptionModel } from 'shared/src'
import { VoiceIsolationModel } from 'shared/src'

export type GenerationModel = '2025-m' | '2026-m' | 'fanar'

export const GENERATION_MODEL_V1: GenerationModel = '2025-m'
export const GENERATION_MODEL_V2: GenerationModel = '2026-m'
export const GENERATION_MODEL_ARABIC_PREVIEW: GenerationModel = 'fanar'

export const DEFAULT_GENERATION_MODEL: GenerationModel = GENERATION_MODEL_V2

export const getGenerationModelOptions = (
  showArabic: boolean,
): { label: string; value: GenerationModel }[] => [
  { label: 'Wave LLM v1', value: '2025-m' },
  { label: 'Wave LLM v2', value: '2026-m' },
  ...(showArabic ? [{ label: 'Arabic Preview', value: GENERATION_MODEL_ARABIC_PREVIEW }] : []),
]

export const generationModelOptions = getGenerationModelOptions(false)

export const MODEL_SAMPLING_DEFAULTS: Record<
  GenerationModel,
  { temperature: number; top_k: number; top_p: number; max_tokens?: number }
> = {
  [GENERATION_MODEL_V1]: { temperature: 0.3, top_k: 50, top_p: 0.95 },
  [GENERATION_MODEL_V2]: { temperature: 1.0, top_k: 64, top_p: 0.95, max_tokens: 2000 },
  [GENERATION_MODEL_ARABIC_PREVIEW]: { temperature: 1.0, top_k: 64, top_p: 0.95, max_tokens: 2000 },
}

export const DEFAULT_TRANSCRIPTION_MODEL: TranscriptionModel = '0226-Medium'

export const ASR_MODEL_ARABIC_PREVIEW = 'asr-2026-ar' as const

export const getTranscriptionModelOptions = (
  showArabic: boolean,
): { label: string; value: TranscriptionModel }[] => [
  { label: 'Wave STT - Legacy', value: '0226-Mini' },
  { label: 'Wave STT', value: '0226-Medium' },
  ...(showArabic ? [{ label: 'Arabic Preview', value: ASR_MODEL_ARABIC_PREVIEW }] : []),
]

export const transcriptionModelOptions = getTranscriptionModelOptions(false)

export const isTranscriptionModel = (value: unknown): value is TranscriptionModel =>
  getTranscriptionModelOptions(true).some((option) => option.value === value)

export const isVoiceIsolationModel = (value: unknown): value is VoiceIsolationModel =>
  Object.values(VoiceIsolationModel).includes(value as VoiceIsolationModel)

export const getVoiceIsolationModelOptions = (
  t: (key: string) => string,
): { label: string; value: VoiceIsolationModel }[] => [
  { label: t('experimental.noise_cancellation.option_none'), value: VoiceIsolationModel.None },
  { label: t('experimental.noise_cancellation.option_dtln'), value: VoiceIsolationModel.Dtln },
  {
    label: t('experimental.noise_cancellation.option_deepfilternet3'),
    value: VoiceIsolationModel.DeepFilterNet3,
  },
]

export const flagSwitches = [
  { key: 'complaint', testId: 'flag-complaint' },
  { key: 'confusion', testId: 'flag-confusion' },
  { key: 'dissatisfaction', testId: 'flag-dissatisfaction' },
  { key: 'fraud', testId: 'flag-fraud' },
  { key: 'hostility', testId: 'flag-hostility' },
  { key: 'ok', testId: 'flag-ok' },
  { key: 'vulgarity', testId: 'flag-vulgarity' },
] as const

export const toolSwitches = [
  { key: 'append_to_session_memory', testId: 'tool-append-to-session-memory' },
  { key: 'end_call', testId: 'tool-end-call' },
  { key: 'set_speech_language', testId: 'tool-set-speech-language' },
  { key: 'text_to_numbers', testId: 'tool-text-to-numbers' },
] as const
